import { ActionEffect, DoActionArgs } from "../../actions/Action";
import { getActionsForAreaByCategory } from "../../actions/Actions";
import { BuildingActionBase } from "../../buildings/Building";
import { PlayerContextState } from "../../PlayerContext";
import { grantResource, Resource } from "../../Resources";
import { registerPostRetirementListener } from "../../Retire";
import { getSecondsPlayed } from "../../timetick/TotalTimePlayed";
import { SpellElement } from "../ElementsAndSchools";
import { SpellActionBase } from "../Spell";

class ConjureBuilding extends SpellActionBase {
  constructor() {
    super();
  }

  getSpellName(): string {
    return "Conjure Building";
  }

  getElement(): SpellElement | undefined {
    return SpellElement.Earth;
  }

  getArea(): string {
    return "Campus";
  }

  getDisplayDescription(): string {
    return "Use magic to apply your current materials towards building a random building.";
  }

  protected getBaseActionEffects(): Record<string, ActionEffect> {
    return {};
  }

  getDisplayEffect(state: PlayerContextState): string {
    return `Builds a random building among the ones that can be built`;
  }

  getManaCost(): number {
    return 300;
  }

  getValidActions(state: PlayerContextState): BuildingActionBase[] {
    const actions = (getActionsForAreaByCategory("Campus")?.["Buildings"] ??
      []) as Record<string, BuildingActionBase>;
    const validActions = Object.values(actions).filter(
      (action) =>
        action.canAfford(state) &&
        action.isVisible(state) &&
        action.isEnabled(state) &&
        (action.getResourceCost(state)?.[Resource.TimePieces] ?? 0) == 0,
    );
    return validActions;
  }

  cached?: [boolean, number] = undefined;

  isEnabled(state: PlayerContextState): boolean {
    const meetsCost = super.isEnabled(state);
    if (!meetsCost) {
      return false;
    }

    // Cache for perf because it is very expensive to get the actions
    if (this.cached !== undefined) {
      const seconds = getSecondsPlayed(state);
      if (seconds - this.cached[1] < 3.0 && seconds > this.cached[1]) {
        return this.cached[0];
      }
    }

    // Need to subtract the spell cost from the Mana
    const cost = this.getCost(state);
    state = grantResource(Resource.Mana, -(cost.resources.Mana ?? 0))(state);
    const validActions = this.getValidActions(state);
    state = grantResource(Resource.Mana, cost.resources.Mana ?? 0)(state);

    const result = validActions.length > 0;
    this.cached = [result, getSecondsPlayed(state)];
    return result;
  }

  getSchoolLevelRequirements(): Record<string, number> {
    return {
      Conjuration: 8,
    };
  }

  doSpellAction(
    state: PlayerContextState,
    args: DoActionArgs,
  ): PlayerContextState {
    const validActions = this.getValidActions(state);
    if (validActions.length === 0) {
      return state;
    }
    const randomBuildingIdx = Math.floor(Math.random() * validActions.length);
    return validActions[randomBuildingIdx].doAction(args, state);
  }
}

const conjureBuilding = new ConjureBuilding();

registerPostRetirementListener(
  "ConjureBuildingCacheClearer",
  (state, isMock) => {
    if (!isMock) {
      conjureBuilding.cached = undefined;
    }
    return state;
  },
);

export default conjureBuilding;
